import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { EARNINGS_CALENDAR_CONFIG } from '../../../shared/services/config';

const EarningsCalendar = ({ token }) => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    symbol: '',
    exchange: '',
  });
  const [columnFilters, setColumnFilters] = useState({
    dateFrom: '',
    dateTo: '',
    symbol: '',
    company: '',
    epsMin: '',
    epsMax: '',
    epsEstMin: '',
    epsEstMax: '',
    epsSurpriseMin: '',
    epsSurpriseMax: '',
    revenueMin: '',
    revenueMax: '',
    revenueEstMin: '',
    revenueEstMax: '',
    revenueSurpriseMin: '',
    revenueSurpriseMax: '',
    time: ''
  });
  const [nullFilters, setNullFilters] = useState({
    company: false,
    time: false,
    eps: true,
    epsEstimated: false,
    epsSurprise: false,
    revenue: true,
    revenueEstimated: false,
    revenueSurprise: false,
  });

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  useEffect(() => {
    applyFilters();
  }, [rows, columnFilters, nullFilters]);

  useEffect(() => {
    const today = new Date();
    const toDate = new Date(today);
    toDate.setDate(today.getDate() + (EARNINGS_CALENDAR_CONFIG.defaultDaysAhead || 10));
    const formatDate = (value) => {
      const year = value.getFullYear();
      const month = `${value.getMonth() + 1}`.padStart(2, '0');
      const day = `${value.getDate()}`.padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const nextFilters = {
      fromDate: formatDate(today),
      toDate: formatDate(toDate),
      symbol: '',
      exchange: '',
    };
    setFilters(nextFilters);
  }, []);

  useEffect(() => {
    if (!filters.fromDate || !filters.toDate) return;
    fetchCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.fromDate, filters.toDate, filters.symbol, filters.exchange]);

  const fetchCalendar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.fromDate) params.append('from', filters.fromDate);
      if (filters.toDate) params.append('to', filters.toDate);
      if (filters.symbol.trim()) params.append('symbol', filters.symbol.trim().toUpperCase());
      if (filters.exchange.trim()) params.append('exchange', filters.exchange.trim().toUpperCase());
      const response = await fetch(`${API_BASE}/api/stock-market/fmp/earnings-calendar?${params.toString()}`, {
        headers: authHeaders,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to load earnings calendar');
      }
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getEpsActual = (row) => row.eps ?? row.epsActual ?? row.eps_actual;
  const getEpsEstimated = (row) =>
    row.epsEstimated ?? row.epsEstimate ?? row.eps_estimated ?? row.eps_estimate;
  const getEpsSurprise = (row) =>
    row.epsSurprise ??
    row.epsSurprisePercent ??
    row.epsSurprisePercentage ??
    row.eps_surprise ??
    row.eps_surprise_percent ??
    row.eps_surprise_percentage;
  const getRevenueActual = (row) => row.revenue ?? row.revenueActual ?? row.revenue_actual;
  const getRevenueEstimated = (row) =>
    row.revenueEstimated ??
    row.revenueEstimate ??
    row.revenue_estimated ??
    row.revenue_estimate;
  const getRevenueSurprise = (row) =>
    row.revenueSurprise ??
    row.revenueSurprisePercent ??
    row.revenueSurprisePercentage ??
    row.revenue_surprise ??
    row.revenue_surprise_percent ??
    row.revenue_surprise_percentage;

  const applyFilters = () => {
    let filtered = [...rows];

    const hasActiveFilters =
      Object.values(columnFilters).some(v => v !== '') || Object.values(nullFilters).some(v => v);

    if (!hasActiveFilters) {
      setFilteredRows(filtered);
      return;
    }

    if (columnFilters.dateFrom) {
      filtered = filtered.filter(row => row.date >= columnFilters.dateFrom);
    }
    if (columnFilters.dateTo) {
      filtered = filtered.filter(row => row.date <= columnFilters.dateTo);
    }
    if (columnFilters.symbol) {
      const s = columnFilters.symbol.toLowerCase();
      filtered = filtered.filter(row => (row.symbol || '').toLowerCase().includes(s));
    }
    if (columnFilters.company) {
      const c = columnFilters.company.toLowerCase();
      filtered = filtered.filter(row => (row.company || row.companyName || row.company_name || '').toLowerCase().includes(c));
    }
    if (nullFilters.company) {
      filtered = filtered.filter(row => (row.company || row.companyName || row.company_name));
    }
    if (nullFilters.time) {
      filtered = filtered.filter(row => row.time != null && row.time !== '');
    }

    const epsSurprise = (row) => getEpsSurprise(row);
    const revenueValue = (row) => getRevenueActual(row);
    const revenueSurprise = (row) => getRevenueSurprise(row);
    const epsValue = (row) => getEpsActual(row);
    const epsEstValue = (row) => getEpsEstimated(row);
    const revenueEstValue = (row) => getRevenueEstimated(row);

    if (nullFilters.eps) {
      filtered = filtered.filter(row => epsValue(row) != null);
    }
    if (nullFilters.epsEstimated) {
      filtered = filtered.filter(row => epsEstValue(row) != null);
    }
    if (nullFilters.epsSurprise) {
      filtered = filtered.filter(row => epsSurprise(row) != null);
    }
    if (nullFilters.revenue) {
      filtered = filtered.filter(row => revenueValue(row) != null);
    }
    if (nullFilters.revenueEstimated) {
      filtered = filtered.filter(row => revenueEstValue(row) != null);
    }
    if (nullFilters.revenueSurprise) {
      filtered = filtered.filter(row => revenueSurprise(row) != null);
    }

    if (columnFilters.epsMin !== '') {
      filtered = filtered.filter(row => Number(epsValue(row)) >= Number(columnFilters.epsMin));
    }
    if (columnFilters.epsMax !== '') {
      filtered = filtered.filter(row => Number(epsValue(row)) <= Number(columnFilters.epsMax));
    }
    if (columnFilters.epsEstMin !== '') {
      filtered = filtered.filter(row => Number(epsEstValue(row)) >= Number(columnFilters.epsEstMin));
    }
    if (columnFilters.epsEstMax !== '') {
      filtered = filtered.filter(row => Number(epsEstValue(row)) <= Number(columnFilters.epsEstMax));
    }
    if (columnFilters.epsSurpriseMin !== '') {
      filtered = filtered.filter(row => Number(epsSurprise(row)) >= Number(columnFilters.epsSurpriseMin));
    }
    if (columnFilters.epsSurpriseMax !== '') {
      filtered = filtered.filter(row => Number(epsSurprise(row)) <= Number(columnFilters.epsSurpriseMax));
    }
    if (columnFilters.revenueMin !== '') {
      filtered = filtered.filter(row => Number(revenueValue(row)) >= Number(columnFilters.revenueMin));
    }
    if (columnFilters.revenueMax !== '') {
      filtered = filtered.filter(row => Number(revenueValue(row)) <= Number(columnFilters.revenueMax));
    }
    if (columnFilters.revenueEstMin !== '') {
      filtered = filtered.filter(row => Number(revenueEstValue(row)) >= Number(columnFilters.revenueEstMin));
    }
    if (columnFilters.revenueEstMax !== '') {
      filtered = filtered.filter(row => Number(revenueEstValue(row)) <= Number(columnFilters.revenueEstMax));
    }
    if (columnFilters.revenueSurpriseMin !== '') {
      filtered = filtered.filter(row => Number(revenueSurprise(row)) >= Number(columnFilters.revenueSurpriseMin));
    }
    if (columnFilters.revenueSurpriseMax !== '') {
      filtered = filtered.filter(row => Number(revenueSurprise(row)) <= Number(columnFilters.revenueSurpriseMax));
    }
    if (columnFilters.time) {
      const t = columnFilters.time.toLowerCase();
      filtered = filtered.filter(row => (row.time || '').toLowerCase().includes(t));
    }

    filtered.sort((a, b) => {
      const aDate = a.date ? new Date(a.date) : null;
      const bDate = b.date ? new Date(b.date) : null;
      if (aDate && bDate) {
        const dateDiff = aDate - bDate;
        if (dateDiff !== 0) return dateDiff;
        const aTime = (a.time || '').toLowerCase();
        const bTime = (b.time || '').toLowerCase();
        if (aTime && bTime) {
          return aTime.localeCompare(bTime);
        }
        if (aTime) return -1;
        if (bTime) return 1;
        return 0;
      }
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    });

    setFilteredRows(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleColumnFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleNullFilterChange = (field, checked) => {
    setNullFilters(prev => ({ ...prev, [field]: checked }));
  };

  const clearColumnFilters = () => {
    setColumnFilters({
      dateFrom: '',
      dateTo: '',
      symbol: '',
      company: '',
      epsMin: '',
      epsMax: '',
      epsEstMin: '',
      epsEstMax: '',
      epsSurpriseMin: '',
      epsSurpriseMax: '',
      revenueMin: '',
      revenueMax: '',
      revenueEstMin: '',
      revenueEstMax: '',
      revenueSurpriseMin: '',
      revenueSurpriseMax: '',
      time: ''
    });
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-white border-bottom">
        <div className="d-flex justify-content-between align-items-center">
          <strong>📅 Earnings Calendar</strong>
          <Button variant="outline-primary" size="sm" onClick={fetchCalendar} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Form.Label>From</Form.Label>
            <Form.Control type="date" value={filters.fromDate} onChange={(e) => handleFilterChange('fromDate', e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label>To</Form.Label>
            <Form.Control type="date" value={filters.toDate} onChange={(e) => handleFilterChange('toDate', e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label>Symbol</Form.Label>
            <Form.Control value={filters.symbol} onChange={(e) => handleFilterChange('symbol', e.target.value)} placeholder="AAPL" />
          </Col>
          <Col md={3}>
            <Form.Label>Exchange</Form.Label>
            <Form.Control value={filters.exchange} onChange={(e) => handleFilterChange('exchange', e.target.value)} placeholder="NASDAQ" />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col md={12} className="d-flex justify-content-end">
            <Button variant="outline-secondary" size="sm" onClick={clearColumnFilters}>
              Clear Column Filters
            </Button>
          </Col>
        </Row>

        <Row className="g-2 mb-3">
          <Col md={12}>
            <div className="d-flex flex-wrap gap-3">
              <Form.Check
                type="checkbox"
                label="Hide null Company"
                checked={nullFilters.company}
                onChange={(e) => handleNullFilterChange('company', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Hide null Time"
                checked={nullFilters.time}
                onChange={(e) => handleNullFilterChange('time', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Hide null EPS"
                checked={nullFilters.eps}
                onChange={(e) => handleNullFilterChange('eps', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Hide null EPS Est"
                checked={nullFilters.epsEstimated}
                onChange={(e) => handleNullFilterChange('epsEstimated', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Hide null EPS Surprise"
                checked={nullFilters.epsSurprise}
                onChange={(e) => handleNullFilterChange('epsSurprise', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Hide null Revenue"
                checked={nullFilters.revenue}
                onChange={(e) => handleNullFilterChange('revenue', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Hide null Rev Est"
                checked={nullFilters.revenueEstimated}
                onChange={(e) => handleNullFilterChange('revenueEstimated', e.target.checked)}
              />
              <Form.Check
                type="checkbox"
                label="Hide null Rev Surprise"
                checked={nullFilters.revenueSurprise}
                onChange={(e) => handleNullFilterChange('revenueSurprise', e.target.checked)}
              />
            </div>
          </Col>
        </Row>

        <div className="table-responsive">
          <Table hover size="sm" className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Date</th>
                <th>Symbol</th>
                <th>Company</th>
                <th className="text-end">EPS</th>
                <th className="text-end">EPS Est</th>
                <th className="text-end">EPS Surprise %</th>
                <th className="text-end">Revenue</th>
                <th className="text-end">Revenue Est</th>
                <th className="text-end">Rev Surprise %</th>
                <th>Time</th>
              </tr>
              <tr>
                <th>
                  <Form.Control size="sm" type="date" value={columnFilters.dateFrom} onChange={(e) => handleColumnFilterChange('dateFrom', e.target.value)} />
                  <Form.Control size="sm" type="date" className="mt-1" value={columnFilters.dateTo} onChange={(e) => handleColumnFilterChange('dateTo', e.target.value)} />
                </th>
                <th>
                  <Form.Control size="sm" value={columnFilters.symbol} onChange={(e) => handleColumnFilterChange('symbol', e.target.value)} placeholder="Symbol" />
                </th>
                <th>
                  <Form.Control size="sm" value={columnFilters.company} onChange={(e) => handleColumnFilterChange('company', e.target.value)} placeholder="Company" />
                </th>
                <th className="text-end">
                  <Form.Control size="sm" type="number" value={columnFilters.epsMin} onChange={(e) => handleColumnFilterChange('epsMin', e.target.value)} placeholder="Min" />
                  <Form.Control size="sm" type="number" className="mt-1" value={columnFilters.epsMax} onChange={(e) => handleColumnFilterChange('epsMax', e.target.value)} placeholder="Max" />
                </th>
                <th className="text-end">
                  <Form.Control size="sm" type="number" value={columnFilters.epsEstMin} onChange={(e) => handleColumnFilterChange('epsEstMin', e.target.value)} placeholder="Min" />
                  <Form.Control size="sm" type="number" className="mt-1" value={columnFilters.epsEstMax} onChange={(e) => handleColumnFilterChange('epsEstMax', e.target.value)} placeholder="Max" />
                </th>
                <th className="text-end">
                  <Form.Control size="sm" type="number" value={columnFilters.epsSurpriseMin} onChange={(e) => handleColumnFilterChange('epsSurpriseMin', e.target.value)} placeholder="Min" />
                  <Form.Control size="sm" type="number" className="mt-1" value={columnFilters.epsSurpriseMax} onChange={(e) => handleColumnFilterChange('epsSurpriseMax', e.target.value)} placeholder="Max" />
                </th>
                <th className="text-end">
                  <Form.Control size="sm" type="number" value={columnFilters.revenueMin} onChange={(e) => handleColumnFilterChange('revenueMin', e.target.value)} placeholder="Min" />
                  <Form.Control size="sm" type="number" className="mt-1" value={columnFilters.revenueMax} onChange={(e) => handleColumnFilterChange('revenueMax', e.target.value)} placeholder="Max" />
                </th>
                <th className="text-end">
                  <Form.Control size="sm" type="number" value={columnFilters.revenueEstMin} onChange={(e) => handleColumnFilterChange('revenueEstMin', e.target.value)} placeholder="Min" />
                  <Form.Control size="sm" type="number" className="mt-1" value={columnFilters.revenueEstMax} onChange={(e) => handleColumnFilterChange('revenueEstMax', e.target.value)} placeholder="Max" />
                </th>
                <th className="text-end">
                  <Form.Control size="sm" type="number" value={columnFilters.revenueSurpriseMin} onChange={(e) => handleColumnFilterChange('revenueSurpriseMin', e.target.value)} placeholder="Min" />
                  <Form.Control size="sm" type="number" className="mt-1" value={columnFilters.revenueSurpriseMax} onChange={(e) => handleColumnFilterChange('revenueSurpriseMax', e.target.value)} placeholder="Max" />
                </th>
                <th>
                  <Form.Control size="sm" value={columnFilters.time} onChange={(e) => handleColumnFilterChange('time', e.target.value)} placeholder="Time" />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-muted py-3">
                    No earnings found. Adjust filters or refresh.
                  </td>
                </tr>
              )}
              {filteredRows.map((row, idx) => (
                <tr key={`${row.symbol}-${row.date}-${idx}`}>
                  <td>{row.date}</td>
                  <td>
                    <a
                      href={`/stock/${row.symbol}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-decoration-none fw-bold"
                    >
                      {row.symbol}
                    </a>
                  </td>
                  <td>{row.company || row.companyName || row.company_name || '-'}</td>
                  <td className="text-end">{getEpsActual(row) ?? '-'}</td>
                  <td className="text-end">{getEpsEstimated(row) ?? '-'}</td>
                  <td className="text-end">
                    <Badge bg={(getEpsSurprise(row) ?? 0) >= 0 ? 'success' : 'danger'}>
                      {getEpsSurprise(row) ?? '-'}
                    </Badge>
                  </td>
                  <td className="text-end">{getRevenueActual(row) ?? '-'}</td>
                  <td className="text-end">{getRevenueEstimated(row) ?? '-'}</td>
                  <td className="text-end">
                    <Badge bg={(getRevenueSurprise(row) ?? 0) >= 0 ? 'success' : 'danger'}>
                      {getRevenueSurprise(row) ?? '-'}
                    </Badge>
                  </td>
                  <td>{row.time || '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default EarningsCalendar;
